import type { Cohort } from "@/app/peopleQueries";
import type { CohortMap, MapRow, MapStep } from "@/app/analyticsQueries";
import { count, plural } from "@/lib/plural";

/** M4 «Когорта»: режимы all / nocohort / <когорта>, карта 29 шагов, статистика —
 *  всё считается из ответа API, ничего не пишется строкой. */

export type CellState = MapRow["cells"][number];
export const SCOPE_ALL = "all";
export const SCOPE_NONE = "nocohort";

export type ScopeTab = { id: string; label: string; count: number };

/** Вкладки режима: «все», когорты с людьми (или активные), «без когорты». Набор без студентов не показываем. */
export function scopeTabs(cohorts: Cohort[] | undefined, noCohort: number | undefined): ScopeTab[] {
  const cs = (cohorts ?? []).filter((c) => c.students > 0 || c.status === "active");
  const total = cs.reduce((s, c) => s + c.students, 0) + (noCohort ?? 0);
  return [
    { id: SCOPE_ALL, label: "все", count: total },
    ...cs.map((c) => ({ id: c.id, label: shortCohort(c.name), count: c.students })),
    { id: SCOPE_NONE, label: "без когорты", count: noCohort ?? 0 },
  ];
}

/** «ML-осень-2026» → «осень-2026» (как в макете). */
export const shortCohort = (name: string) => name.replace(/^ML-/, "");

export function scopeLabel(scope: string, cohorts?: Cohort[]): string {
  if (scope === SCOPE_ALL) return "Все студенты";
  if (scope === SCOPE_NONE) return "Без когорты";
  return cohorts?.find((c) => c.id === scope)?.name ?? "Когорта";
}

export const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}.${m}.${y}` : iso;
};

/** Подзаголовок режима: для когорты — старт, неделя и менторы — из данных. */
export function scopeSub(scope: string, map: CohortMap, cohorts?: Cohort[]): string {
  if (scope === SCOPE_ALL) return `${count(map.rows.length, "человек", "человека", "человек")} во всех когортах и вне их`;
  if (scope === SCOPE_NONE) return "ведутся индивидуально, без общего старта";
  const c = cohorts?.find((x) => x.id === scope);
  const parts: string[] = [];
  if (c?.starts_on) parts.push(`старт ${fmtDate(c.starts_on)}`);
  if (c?.status === "finished") parts.push("завершена");
  else if (c && c.week > 0) parts.push(`${c.week}-я неделя`);
  else if (c) parts.push("ещё не началась");
  const mentors = mentorNames(map.rows);
  if (mentors.length) parts.push(`${plural(mentors.length, "ментор", "менторы", "менторы")} ${[...mentors].sort((a, b) => a.localeCompare(b, "ru")).join(", ")}`);
  return parts.join(" · ");
}

/** Менторы в порядке появления (без «не назначен»). */
export function mentorNames(rows: MapRow[]): string[] {
  const out: string[] = [];
  for (const r of rows) if (r.mentor && !out.includes(r.mentor)) out.push(r.mentor);
  return out;
}

/** Цвет ментора — по порядку появления; имя всегда написано текстом. */
export function mentorTone(name: string, order: string[]): string {
  if (!name) return "text-mut";
  const i = order.indexOf(name);
  return i === 0 ? "text-acc" : i === 1 ? "text-info" : "text-ink2";
}

export type TrackGroup = { track: string; from: number; n: number };

/** Шаги → группы треков для шапки карты (span n). */
export function trackGroups(steps: MapStep[]): TrackGroup[] {
  const out: TrackGroup[] = [];
  steps.forEach((s, i) => {
    const last = out[out.length - 1];
    if (last && last.track === s.track) last.n++;
    else out.push({ track: s.track, from: i, n: 1 });
  });
  return out;
}

export const stepName = (s: MapStep) => `${s.track} · ${s.title}`;

/** Подсказка клетки: «Анна Ковалёва: ML · EDA · в работе». */
export const cellTitle = (r: MapRow, s: MapStep, state: CellState) => `${r.name}: ${stepName(s)} · ${state}`;

/** Вид клетки по состоянию — из легенды макета. Серая «не начат» — пунктир. */
export const CELL_LOOK: Record<CellState, string> = {
  "сдан": "border-acc bg-acc",
  "в работе": "border-accdim bg-accsoft",
  "застрял": "border-err bg-errsoft",
  "не начат": "border-dashed border-bd2 bg-transparent",
};

export const LEGEND: { state: CellState; label: string }[] = [
  { state: "сдан", label: "гейт сдан" },
  { state: "в работе", label: "в работе" },
  { state: "застрял", label: "застрял дольше 10 дней" },
  { state: "не начат", label: "не начат" },
];

export type Stat = { n: string; label: string; note: string; tone: string; testId: string };

/** Три карточки сверху — из карты. */
export function stats(map: CohortMap, scope: string): Stat[] {
  const n = map.rows.length;
  const total = map.steps.length;
  return [
    {
      n: String(n),
      label: plural(n, "студент", "студента", "студентов"),
      note: scope === SCOPE_ALL ? "во всех когортах и вне их" : scope === SCOPE_NONE ? "ведутся индивидуально" : "в когорте",
      tone: "text-ink",
      testId: "stat-students",
    },
    { n: n ? `${map.median_steps} из ${total}` : "—", label: "медиана шагов", note: `шагов в маршруте: ${total}`, tone: "text-acc", testId: "stat-median" },
    {
      n: String(map.stuck),
      label: plural(map.stuck, "застрял", "застряли", "застряли"),
      note: "дольше 10 дней на шаге",
      tone: map.stuck ? "text-err" : "text-ok",
      testId: "stat-stuck",
    },
  ];
}

export const people = (n: number) => count(n, "человек", "человека", "человек");

/** Пустое состояние — текст зависит от режима. */
export function emptyBody(scope: string): string {
  if (scope === SCOPE_NONE) return "Все студенты распределены по когортам. Когорты необязательны: студента можно вести и без потока — он появится здесь.";
  if (scope === SCOPE_ALL) return "В школе пока нет студентов. Пригласите их в разделе «Люди» — карта появится сразу после принятия приглашения.";
  return "В этой когорте пока нет студентов. Добавьте их в разделе «Люди» или дождитесь принятия приглашений.";
}
